import { getUrlWithVariant, ProductForm } from '@shopify/theme-product-form';
import { formatMoney } from '@shopify/theme-currency';
import { addToCartForm, changeCart } from '../utils/cart';

/**
 * ProductFormBase is a class that is meant to be extended by other classes.
 * It contains the base logic for product forms.
 * To make modifications that are unique to the product main or product card form,
 * You should make the modifications in the ProductMainFormElement or ProductCardFormElement classes.
 */
export class ProductFormBase extends HTMLElement {
  constructor() {
    super();

    this.productWrapper = this.closest("[data-product-wrapper]");

    this.priceWrapperElement = this.productWrapper.querySelector(".price");
    this.priceElement = Array.from(this.priceWrapperElement.querySelectorAll("[data-price]"));
    this.comparePriceElement = Array.from(this.priceWrapperElement.querySelectorAll("[data-compare-at-price]"));

    this.skuContainer = this.productWrapper.querySelector("[data-product-sku-container]");
    this.skuElement = this.productWrapper.querySelector("[data-product-sku]");

    this.productFormElement = this.querySelector('form');
    this.submitButton = this.querySelector('[data-submit-product]');
    this.submitButtonText = this.submitButton.querySelector('.button__text');
    this.shopPaymentButton = this.querySelector('.shopify-payment-button-wrapper');
    this.selectId = this.querySelector('[name="id"]');
    this.quantityInput = this.querySelector('[name="quantity"]');

    this.allSwatches = Array.from(this.querySelectorAll('[data-option-position]'));
    this.allSelectOptions = Array.from(this.querySelectorAll('[data-option-position-select]'));

    this.sellingPlanId = this.querySelector('[data-selling-plan-id]');
    this.variantSellingPlans = this.querySelectorAll('[data-variant-selling-plans]');

    if (this.sellingPlanId) {
      this.querySelectorAll('[data-variant-selling-plans] input').forEach(input => {
        input.addEventListener('change', this.updateSelectedSellingPlan.bind(this));
      });
    }

    if (this.productFormElement) {
      this.initializeForm();

      this.productFormElement.addEventListener('submit', this.submitProductForm.bind(this));
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.updatePerItemPrice.bind(this));
    }
  }

  async initializeForm() {
    this.product = this.getProductJson();

    this.productForm = new ProductForm(this.productFormElement, this.product, {
      onOptionChange: this.optionsChange.bind(this),
    });
  }

  getProductJson() {
    return JSON.parse(this.querySelector('[data-product]').innerHTML);
  }

  async submitProductForm(event) {
    event.preventDefault();

    this.submitButton.classList.add('loading');

    if (this.keyToRemove) {
      await changeCart(this.keyToRemove, 0, true);
    }

    await addToCartForm(this.productFormElement);

    this.submitButton.classList.remove('loading');
  }

  updateBrowserHistory(variant) {
    if (!variant) {
      return;
    }

    const url = getUrlWithVariant(window.location.href, variant.id);
    window.history.replaceState({path: url}, '', url);
  }

  updateMainSelect(variant) {
    if (variant) {
      this.selectId.value = variant.id;
      this.selectId.dispatchEvent(new Event('change'));
    }
  }

  updateNullOptions(variantOption, elementOption) {
    if (elementOption && elementOption.tagName === 'OPTION') {
      elementOption.parentElement.querySelectorAll('option').forEach(option => {
        if (option.value === variantOption) {
          option.setAttribute('selected', 'selected');
          option.classList.contains('hide') ? option.classList.remove('hide') : null;
        } else {
          option.removeAttribute('selected');
        }
      });
      elementOption.parentElement.value = variantOption;
    } else {
      elementOption.closest('.swatches')?.querySelectorAll('input').forEach(input => {
        if (input.value === variantOption) {
          input.checked = true;
        } else {
          input.removeAttribute('checked');
        }
      });
    }
  }

  updateOtherOptions(variant) {
    if (variant?.option1) {
      const option1Element = this.querySelector(`input[value="${variant.option1}"]`) || this.querySelector(`option[value="${variant.option1}"]`);
      this.updateNullOptions(variant.option1, option1Element);
    }
    if (variant?.option2) {
      const option2Element = this.querySelector(`input[value="${variant.option2}"]`) || this.querySelector(`option[value="${variant.option2}"]`);
      this.updateNullOptions(variant.option2, option2Element);
    }
    if (variant?.option3) {
      const option3Element = this.querySelector(`input[value="${variant.option3}"]`) || this.querySelector(`option[value="${variant.option3}"]`);
      this.updateNullOptions(variant.option3, option3Element);
    }
  }

  updateProductPrice(variant) {
    this.priceWrapperElement.classList.toggle('hide', !variant);

    if (variant) {
      this.priceElement.forEach(price => price.innerHTML = formatMoney(variant.price, theme.moneyFormat).replace('.00', ''));
      this.comparePriceElement.forEach(compareAtPrice => compareAtPrice.innerHTML = formatMoney(variant.compare_at_price, theme.moneyFormat).replace('.00', ''));

      if (variant.compare_at_price > variant.price) {
        this.priceWrapperElement.classList.add('price--on-sale');
      } else {
        this.priceWrapperElement.classList.remove('price--on-sale');
      }

      if (variant.available) {
        this.priceWrapperElement.classList.remove('price--sold-out');
      } else {
        this.priceWrapperElement.classList.add('price--sold-out');
      }
    }
  }

  updateSku(variant) {
    if (variant && this.skuElement && variant.sku) {
      this.skuElement.innerHTML = variant.sku;
    }

    if (this.skuContainer) {
      this.skuContainer.classList.toggle('hide', !variant || !variant.sku);
    }
  }

  updateSelectedOptions(variant) {
    this.allSwatches.filter(swatch => swatch.checked).forEach(swatch => {
      const optionValue = swatch.value;
      const selectedOptionElement = swatch.closest('[data-option-container]').querySelector('[data-selected-option]');

      if (selectedOptionElement && this.dataset.template?.includes("gift-card")) {
        const variantPrice = formatMoney(variant.price, theme.moneyFormat).replace('.00', '');
        selectedOptionElement.innerHTML = variantPrice;
      } else if (selectedOptionElement) {
        selectedOptionElement.innerHTML = this.removeVariantCodes(optionValue);
      }
    });

    this.allSelectOptions.filter(select => select.value).forEach(select => {
      const optionValue = select.value;

      const selectedOptionElement = select.closest('[data-option-container]').querySelector('[data-selected-option]');
      if (selectedOptionElement) {
        selectedOptionElement.innerHTML = this.removeVariantCodes(optionValue);
      }
    });
  }

  updateProductButton(variant) {

    this.klaviyoBisTrigger = this.querySelector('.klaviyo-bis-trigger');
    if (this.klaviyoBisTrigger && this.klaviyoBisTrigger.classList.contains('hidden')) {
      this.klaviyoBisTrigger.classList.remove('hidden');
    }

    let customUnavailable = false;
    const variantDetail = this.product.variant_details?.find((variantFind) => variant.id == variantFind.id);
    let lowStock = variantDetail?.low_stock === "true";
    const lastTwoDigits = variant.price.toString().slice(-2);
    const variantDisabled = variantDetail?.variant_disabled === "true";
    if (lastTwoDigits === '97' || variantDisabled) {
      customUnavailable = true;
    }

    this.submitButtonText.innerHTML = "";

    if (!variant || customUnavailable) {
      this.submitButton.disabled = true;
      this.submitButtonText.innerHTML = theme.strings.product.unavailable;
    } else if (variant.available) {
      if (lowStock) {
        this.submitButton.disabled = true;
        this.submitButtonText.innerHTML = theme.strings.product.unavailable;

        if (this.shopPaymentButton) {
          this.shopPaymentButton.classList.add('hide');
        }
      } else {
        this.submitButton.disabled = false;
        this.submitButtonText.innerHTML = theme.strings.product.addToCart;

        if (this.shopPaymentButton) {
          this.shopPaymentButton.classList.remove('hide');
        }
      }

      if (this.keyToRemove) {
        this.submitButtonText.innerHTML = theme.strings.product.saveChanges;
      }
    } else {
      this.submitButton.disabled = true;
      this.submitButtonText.innerHTML = theme.strings.product.soldOut;

      if (this.shopPaymentButton) {
        this.shopPaymentButton.classList.add('hide');
      }

    }
  }

  updateOutOfStockOptions() {
    const $option1 = this.querySelector('input[id^=Option1]:checked') || this.querySelector('select[id^=Option1]');
    const $option2 = this.querySelector('input[id^=Option2]:checked') || this.querySelector('select[id^=Option2]');

    const option1 = $option1 ? $option1.value : null;
    const option2 = $option2 ? $option2.value : null;

    // need to update this ⬇
    /**
     * we check the status of every swatch.
     *
     * For the first option we just check if any variant is available with that value.
     *
     * For the second option we check if any variant is available with that value and the currently selected first option.
     *
     * For the third option we check if any variant is available with that value and the currently selected first and second options.
     */
    this.allSwatches.forEach(optionInput => {
      if (optionInput.dataset.optionPosition == 1) {
        var variantExists = true;

        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === optionInput.value &&
            variantCompare.available
        );
      } else if (optionInput.dataset.optionPosition == 2) {
        var variantExists = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === option1 &&
            variantCompare.option2 === optionInput.value
        );

        if (variantExists) {
          var availableWithValue = variantExists;
        }
      } else if (optionInput.dataset.optionPosition == 3) {
        var variantExists = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === option1 &&
            variantCompare.option2 === option2 &&
            variantCompare.option3 === optionInput.value
        );

        if (variantExists) {
          var availableWithValue = variantExists;
        }
      }

      let variantDetail = null;
      if (availableWithValue) {
        variantDetail = this.product.variant_details.find(detail => detail.id == availableWithValue.id);
      }

      optionInput.disabled = !variantExists;
      optionInput.closest(".swatches__swatch").classList.toggle('hide', !variantExists); // <- keep this?

      if (optionInput.hasAttribute('data-is-color')) {
        optionInput.classList.toggle('swatches__disabled', variantDetail?.low_stock === "true");
      }
    });

    this.allSelectOptions.forEach(select => {
      if (select.dataset.optionPositionSelect == 1) {
        // Don't really need to do anything here
      } else if (select.dataset.optionPositionSelect == 2) {
        Array.from(select.options).forEach(option => {
          let variantExists = this.product.variants.find(
            variant =>
            variant.option1 === option1 &&
            variant.option2 === option.value
          )

          if (variantExists && variantExists.available) {
            option.classList.remove('hide');
            option.classList.remove('disabled');
            option.removeAttribute('disabled');
          } else {
            option.classList.add('hide');
            option.classList.add('disabled');
            option.setAttribute('disabled', true);
          }
          option.innerHTML = variantExists?.available ? `${this.removeVariantCodes(option.value)}` : `${this.removeVariantCodes(option.value)} - ${theme.strings.product.soldOut}`
        });
      } else if (select.dataset.optionPositionSelect == 3) {
        Array.from(select.options).forEach(option => {
          let variantExists = this.product.variants.find(
            variant =>
            variant.option1 === option1 &&
            variant.option2 === option2 &&
            variant.option3 === option.value
          )

          if (variantExists && variantExists.available) {
            option.classList.remove('hide');
            option.classList.remove('disabled');
            option.removeAttribute('disabled');
          } else {
            option.classList.add('hide');
            option.classList.add('disabled');
            option.setAttribute('disabled', true);
          }
          option.innerHTML = variantExists?.available ? `${this.removeVariantCodes(option.value)}` : `${this.removeVariantCodes(option.value)} - ${theme.strings.product.soldOut}`
        });
      }
    });
  }

  updateVisibleSellingPlans(variant) {
    if (variant.selling_plan_allocations.length === 0) {
      this.sellingPlanId.value = '';
    }

    this.variantSellingPlans.forEach(sellingPlan => {
      if (variant.id == sellingPlan.dataset.variantSellingPlans) {
        sellingPlan.classList.remove('hide');

        if (this.sellingPlanId) {
          this.sellingPlanId.value = sellingPlan.querySelector('input:checked').value;
        }
      } else {
        sellingPlan.classList.add('hide');
      }
    });
  }

  updateSelectedSellingPlan(event) {
    this.sellingPlanId.value = event.target.value;
  }

  updatePerItemPrice() {
    if (this.product.variant_price_breaks) {
      const currentVariant = this.product.variants.find(variant => variant.id == this.selectId.value);
      const variantVolumePricing = this.product.variant_price_breaks.find(variant => variant.id == currentVariant.id).quantity_price_breaks;
      const quantity = parseInt(this.quantityInput.value);

      const priceBreak = variantVolumePricing.findLast(pricing => quantity >= pricing.minimum_quantity);
      const perItemPrice = priceBreak ? priceBreak.price : currentVariant.price;

      this.priceElement.forEach(price => price.innerHTML = formatMoney(perItemPrice, theme.moneyFormat).replace('.00', ''));
    }
  }

  removeVariantCodes(string) {
    const regex = /\([^)]*\)/g;

    const formatedString = string.replace(regex, '').trim();
    return formatedString
  }
}
